import { useCallback, RefObject } from 'react';
import type { Lead, Option } from '@de/types/types';
import { useDynamicExperienceContext } from '@de/providers/DynamicExperienceProvider';
import {
  createFieldEventsPayload,
  createStepElementViewedPayload,
} from '@de/utils/createEventingPayloadsDE';
import useFlowTrackingEvents from '@de/hooks/useFlowTrackingEvents';
import useViewEvents from '@de/hooks/useViewEvents';

export interface UseQuestionEventsParams {
  /** Step / question id used to look up flow-state metadata. */
  id: string;
  /** Tagular `fieldType` (also used as the StepElementViewed `variant` fallback). */
  fieldType: string;
  /** Options rendered by the variant — used to build the FieldViewed `fieldValue` (joined list). */
  options: Option[];
  /** Question title; used as `fieldLabel` / fallback `stepName` for multi-question children. */
  title?: string;
  /** Step name from props; preferred `fieldName` source. */
  stepName?: string;
  /** When true, payloads include `stepId` / `stepName` overrides for multi-question children. */
  isMultiQuestionChild?: boolean;
  /** Optional explicit `variant` override for StepElementViewed; defaults to `fieldType`. */
  variant?: string;
  /** Lead field associated with the question; used as `fieldId` and `fieldName` fallback. */
  leadFieldAssociated?: keyof Lead;
}

export interface UseQuestionEventsResult {
  /** Attach to the variant's outermost element to fire StepElementViewed + FieldViewed on intersect. */
  ref: RefObject<HTMLElement | null>;
  /** Fire FieldInputted with the variant-computed `fieldValue` (single text or joined list). */
  triggerFieldInputted: (fieldValue: string) => void;
}

/**
 * Shared question-events hook for Step variants (single/multiple choice, radio,
 * checkbox, dropdown, toggle, dynamic*). Centralizes the StepElementViewed +
 * FieldViewed view-events and the FieldInputted click-event payloads that were
 * previously duplicated across every variant component.
 *
 * TextQuestion is intentionally NOT a consumer — it has bespoke debounce, PII
 * sanitization, and conditional FieldViewed shape.
 */
const useQuestionEvents = ({
  id,
  fieldType,
  options,
  title,
  stepName,
  isMultiQuestionChild = false,
  variant,
  leadFieldAssociated,
}: UseQuestionEventsParams): UseQuestionEventsResult => {
  const { eventContext, getStepMetadataById } = useDynamicExperienceContext();
  const { triggerFieldEvents } = useFlowTrackingEvents();
  const stepData = getStepMetadataById(id || '');

  const { ref } = useViewEvents([
    createStepElementViewedPayload({
      ...eventContext,
      variant: variant || fieldType,
    }),
    createFieldEventsPayload(
      {
        ...eventContext,
        fieldName:
          stepName ||
          stepData?.leadFieldAssociated?.toString() ||
          leadFieldAssociated?.toString() ||
          '',
        fieldValue: options.map((opt) => opt.text as string).join(','),
        fieldType,
        ...(isMultiQuestionChild && {
          stepId: id,
          stepName: stepName || title,
        }),
      },
      'FieldViewed',
    ),
  ]);

  const triggerFieldInputted = useCallback(
    (fieldValue: string) => {
      triggerFieldEvents('FieldInputted', {
        ...eventContext,
        fieldValue,
        fieldNumber: eventContext?.currentStepIndex || 0,
        fieldName: isMultiQuestionChild
          ? stepName || ''
          : eventContext?.currentStep?.stepName || '',
        fieldLabel: isMultiQuestionChild
          ? title
          : eventContext?.currentStep?.title,
        fieldType,
        fieldId: isMultiQuestionChild
          ? leadFieldAssociated?.toString() || id
          : eventContext?.currentStep?.leadFieldAssociated?.toString() || '',
        ...(isMultiQuestionChild && {
          stepId: id,
          stepName: stepName || title,
        }),
      });
    },
    [
      eventContext,
      fieldType,
      id,
      isMultiQuestionChild,
      leadFieldAssociated,
      stepName,
      title,
      triggerFieldEvents,
    ],
  );

  return { ref, triggerFieldInputted };
};

export default useQuestionEvents;
